<template>
  <div
    class="search_engine"
    :class="{ 'add-overflow-y': this.$route.name !== 'search' }"
  >
    <b-modal
      :active.sync="loader.isLoading"
      :can-cancel="[false, false, false]"
    >
      <div
        class="loading-prompt"
        data-cy="search-loading-prompt"
        style="width: 100%; display: flex; justify-content: center;"
      >
        <clip-loader
          class="prompt-loader"
          :loading="true"
          color="#949494"
          size="100px"
        ></clip-loader>
        <b class="prompt-header"> <br />Searching flights. </b>
      </div>
    </b-modal>
    <div
      style="box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px; margin-bottom: 40px; "
      v-if="isMaintainence && this.$route.name === 'search'"
    >
      <b-message title="Please note:" aria-close-label="Close message">
        {{ maintenanceMessage }}
      </b-message>
    </div>

    <div class="search-engine-heading" data-cy="search-engine-heading">
      <h1>Search for flights</h1>
    </div>
    <div class="radio-button-wrapper">
      <b-radio
        ref="input"
        :key="typeKey"
        v-model="tripType"
        name="tripType"
        native-value="return"
        data-cy="trip-type-return"
        >Return</b-radio
      >
      <b-radio
        v-model="tripType"
        name="tripType"
        native-value="oneway"
        data-cy="trip-type-oneway"
        >One-way</b-radio
      >
      <b-radio
        v-model="tripType"
        name="tripType"
        native-value="multi"
        data-cy="trip-type-multi"
        >Multi-city</b-radio
      >
    </div>

    <!--Pickup and dropoff locations-->
    <transition name="slide-in">
      <div
        class="location-wrapper"
        v-if="tripType !== 'multi'"
        style="position: relative;"
      >
        <label
          class="floating-label departure-location-label"
          :class="{
            'floating-label-active':
              departureLocation.airportCode ||
              departureInputFocused ||
              departureHasText,
          }"
        >
          {{
            departureLocation.airportCode ||
            departureInputFocused ||
            departureHasText
              ? "From"
              : "From. . ."
          }}
        </label>
        <b-autocomplete
          :data="filteredAirports"
          ref="departureLocation"
          placeholder=""
          :keep-first="true"
          field="airportName"
          :custom-formatter="formatAirportLabel"
          :open-on-focus="true"
          autocomplete="new-password"
          data-cy="departure-input"
          :class="{
            'has-active-label':
              departureLocation.airportCode ||
              departureInputFocused ||
              departureHasText,
          }"
          @typing="
            (value) => {
              getAsyncAirports(value);
              departureHasText = value && value.length > 0;
            }
          "
          @focus="departureInputFocused = true"
          @blur="departureInputFocused = false"
          @select="
            (option) => {
              if (option) {
                departureAirportOnly = option.airportName
                  .toLowerCase()
                  .includes('all')
                  ? false
                  : true;
                departureLocation = option;
                departureHasText = false;
                filteredAirports = [];
                this.focusArrivalLocation();
              } else {
                // Handle the case where the input is cleared
                departureLocation = {};
                departureAirportOnly = false;
                departureHasText = false;
              }
            }
          "
        >
          <template slot-scope="props">
            <airport-option :location="props.option" />
          </template>
        </b-autocomplete>
        <label
          class="floating-label arrival-location-label"
          :class="{
            'floating-label-active':
              arrivalLocation.airportCode ||
              arrivalInputFocused ||
              arrivalHasText,
          }"
        >
          {{
            arrivalLocation.airportCode || arrivalInputFocused || arrivalHasText
              ? "To"
              : "To. . ."
          }}
        </label>
        <b-autocomplete
          :data="filteredAirports"
          placeholder=""
          field="airportName"
          :custom-formatter="formatAirportLabel"
          :keep-first="true"
          ref="arrivalLocation"
          :open-on-focus="true"
          autocomplete="new-password"
          data-cy="arrival-input"
          :class="{
            'has-active-label':
              arrivalLocation.airportCode ||
              arrivalInputFocused ||
              arrivalHasText,
          }"
          @typing="
            (value) => {
              getAsyncAirports(value);
              arrivalHasText = value && value.length > 0;
            }
          "
          @focus="arrivalInputFocused = true"
          @blur="arrivalInputFocused = false"
          @select="
            (option) => {
              if (option) {
                arrivalAirportOnly = option.airportName
                  .toLowerCase()
                  .includes('all')
                  ? false
                  : true;
                arrivalLocation = option;
                arrivalHasText = false;
                filteredAirports = [];
                this.focusOutboundDate();
              } else {
                // Handle the case where the input is cleared
                arrivalLocation = {};
                arrivalAirportOnly = false;
                arrivalHasText = false;
              }
            }
          "
        >
          <template slot-scope="props">
            <airport-option :location="props.option" />
          </template>
        </b-autocomplete>
      </div>
    </transition>

    <!-- Datepickers -->
    <transition name="slide-in">
      <span
        class="time-picker-container"
        v-if="
          tripType !== 'multi' &&
            departureLocation.airportCode &&
            arrivalLocation.airportCode
        "
      >
        <!--One way case-->
        <div
          v-if="tripType == 'oneway'"
          class="time-picker-container one-way-only"
          style="position: relative;"
        >
          <label
            class="floating-label"
            :class="{
              'floating-label-active': departureDate || departureDateModalOpen,
            }"
          >
            {{
              departureDate || departureDateModalOpen
                ? "Departure date"
                : "Departure date. . ."
            }}
          </label>
          <CustomDatepickerV3
            v-model="departureDate"
            id="one-way-only"
            ref="outboundDate"
            locale="en-GB"
            placeholder=""
            :input-props="{
              placeholder: '',
            }"
            :min-date="minDepartureDate"
            :isOneWay="true"
            data-cy="departure-date-input"
            :class="{
              'has-active-label': departureDate || departureDateModalOpen,
            }"
            @input="
              (date) => {
                tripType == 'oneway' ? focusSearch() : null;
                onDepartureDatePickerClose();
              }
            "
            @click.native="onDepartureDatePickerOpen"
          >
            <img
              src="@/assets/images/icons/finance-calendar.png"
              alt="calendar icon"
            />
          </CustomDatepickerV3>
        </div>
        <!-- Return case -->
        <div
          v-if="tripType == 'return' || tripType == 'multi'"
          class="time-picker-container"
          style="position: relative;"
        >
          <label
            class="floating-label departure-label"
            :class="{
              'floating-label-active': departureDate || departureDateModalOpen,
            }"
          >
            {{
              departureDate || departureDateModalOpen
                ? "Departure date"
                : "Departure date. . ."
            }}
          </label>
          <CustomDatepickerV3
            v-model="departureDate"
            :endValue.sync="returnDate"
            id="datepicker-departure"
            ref="outboundDate"
            locale="en-GB"
            placeholder=""
            :input-props="{
              placeholder: '',
            }"
            :min-date="minDepartureDate"
            :isOneWay="false"
            data-cy="departure-date-input"
            :class="{
              'has-active-label': departureDate || departureDateModalOpen,
            }"
            @dateRangeSelected="
              (startDate, endDate) => {
                handleDateRangeSelection(startDate, endDate);
                onDepartureDatePickerClose();
              }
            "
            @click.native="onDepartureDatePickerOpen"
          >
            <img
              src="@/assets/images/icons/finance-calendar.png"
              alt="calendar icon"
            />
          </CustomDatepickerV3>

          <label
            class="floating-label return-label"
            :class="{
              'floating-label-active': returnDate || returnDateModalOpen,
            }"
          >
            {{
              returnDate || returnDateModalOpen
                ? "Return date"
                : "Return date. . ."
            }}
          </label>
          <CustomDatepickerV3
            v-model="returnDate"
            :endValue.sync="departureDate"
            id="datepicker-return"
            ref="returnDate"
            locale="en-GB"
            placeholder=""
            :input-props="{
              placeholder: '',
            }"
            :min-date="departureDate ? departureDate : new Date()"
            :disabled-dates="[
              {
                start: null,
                end: new Date(new Date().setDate(new Date().getDate() - 1)),
              },
            ]"
            :key="refreshKey + 'end'"
            :isOneWay="false"
            :isReturnDateField="true"
            data-cy="return-date-input"
            :class="{ 'has-active-label': returnDate || returnDateModalOpen }"
            @dateRangeSelected="
              (startDate, endDate) => {
                handleDateRangeSelection(startDate, endDate);
                onReturnDatePickerClose();
              }
            "
            @click.native="onReturnDatePickerOpen"
          >
            <img
              src="@/assets/images/icons/finance-calendar.png"
              alt="calendar icon"
            />
          </CustomDatepickerV3>
        </div>
      </span>
    </transition>

    <!-- Multi City Trips -->
    <transition name="slide-in">
      <div v-if="tripType == 'multi'">
        <b-field
          v-for="(flight, index) in multiFlights"
          :key="flight.key"
          grouped
          group-multiline
          expanded
          class="has-field-margin"
          :label="`Leg ${index + 1}`"
        >
          <hr class="multi-divider" />
          <div class="location-wrapper" style="position: relative;">
            <b-field expanded style="margin-bottom: 5px;">
              <label
                class="floating-label departure-location-label"
                :class="{
                  'floating-label-active':
                    (flight.departureLocation &&
                      flight.departureLocation.airportCode) ||
                    flight.departureInputFocused ||
                    flight.departureHasText,
                }"
              >
                {{
                  (flight.departureLocation &&
                    flight.departureLocation.airportCode) ||
                  flight.departureInputFocused ||
                  flight.departureHasText
                    ? "From"
                    : "From. . ."
                }}
              </label>
              <b-autocomplete
                :data="filteredAirports"
                placeholder=""
                :keep-first="true"
                :open-on-focus="true"
                field="airportName"
                :custom-formatter="formatAirportLabel"
                autocomplete="new-password"
                :class="{
                  'has-active-label':
                    (flight.departureLocation &&
                      flight.departureLocation.airportCode) ||
                    flight.departureInputFocused ||
                    flight.departureHasText,
                }"
                :value="getAutocompleteValue(flight.departureLocation)"
                @typing="
                  (value) => {
                    getAsyncAirports(value);
                    flight.departureHasText = value && value.length > 0;
                    // Clear the location object when input is manually emptied
                    if (!value || value.length === 0) {
                      flight.departureLocation = {};
                    }
                  }
                "
                @focus="flight.departureInputFocused = true"
                @blur="flight.departureInputFocused = false"
                @select="
                  (option) => {
                    flight.departureAirportOnly = option.airportName
                      .toLowerCase()
                      .includes('all')
                      ? false
                      : true;
                    flight.departureLocation = option;
                    flight.departureHasText = false;
                    filteredAirports = [];
                  }
                "
              >
                <template slot-scope="props">
                  <airport-option :location="props.option" />
                </template>
              </b-autocomplete>
            </b-field>
            <b-field expanded style="margin-bottom: 5px;">
              <label
                class="floating-label arrival-location-label"
                :class="{
                  'floating-label-active':
                    (flight.arrivalLocation &&
                      flight.arrivalLocation.airportCode) ||
                    flight.arrivalInputFocused ||
                    flight.arrivalHasText,
                }"
              >
                {{
                  (flight.arrivalLocation &&
                    flight.arrivalLocation.airportCode) ||
                  flight.arrivalInputFocused ||
                  flight.arrivalHasText
                    ? "To"
                    : "To. . ."
                }}
              </label>
              <b-autocomplete
                :data="filteredAirports"
                placeholder=""
                field="airportName"
                :custom-formatter="formatAirportLabel"
                :keep-first="true"
                ref="arrivalLocation"
                :open-on-focus="true"
                autocomplete="new-password"
                :class="{
                  'has-active-label':
                    (flight.arrivalLocation &&
                      flight.arrivalLocation.airportCode) ||
                    flight.arrivalInputFocused ||
                    flight.arrivalHasText,
                }"
                :value="getAutocompleteValue(flight.arrivalLocation)"
                @typing="
                  (value) => {
                    getAsyncAirports(value);
                    flight.arrivalHasText = value && value.length > 0;
                    // Clear the location object when input is manually emptied
                    if (!value || value.length === 0) {
                      flight.arrivalLocation = {};
                    }
                  }
                "
                @focus="flight.arrivalInputFocused = true"
                @blur="flight.arrivalInputFocused = false"
                @select="
                  (option) => {
                    flight.arrivalAirportOnly = option.airportName
                      .toLowerCase()
                      .includes('all')
                      ? false
                      : true;
                    flight.arrivalLocation = option;
                    flight.arrivalHasText = false;
                    filteredAirports = [];
                    onArrivalLocationChange(index, option);
                  }
                "
              >
                <template slot-scope="props">
                  <airport-option :location="props.option" />
                </template>
              </b-autocomplete>
            </b-field>
          </div>
          <transition name="slide-in">
            <b-field
              class="last multicity-date-input"
              expanded
              v-if="
                tripType === 'multi' &&
                  flight.departureLocation &&
                  flight.arrivalLocation
              "
            >
              <div style="position: relative;">
                <label
                  class="floating-label"
                  :class="{
                    'floating-label-active':
                      flight.departureDate || flight.dateModalOpen,
                  }"
                >
                  {{
                    flight.departureDate || flight.dateModalOpen
                      ? "Departure date"
                      : "Departure date. . ."
                  }}
                </label>
                <CustomDatepickerV3
                  v-model="flight.departureDate"
                  id="multi-city"
                  ref="outboundDate"
                  locale="en-GB"
                  placeholder=""
                  :input-props="{
                    placeholder: '',
                  }"
                  :disabled-dates="[
                    {
                      start: null,
                      end: new Date(
                        new Date().setDate(new Date().getDate() - 1),
                      ),
                    },
                  ]"
                  :min-date="lastDate(index)"
                  :isOneWay="true"
                  :class="{
                    'has-active-label':
                      flight.departureDate || flight.dateModalOpen,
                  }"
                  @input="
                    (date) => {
                      tripType == 'multi-city' ? focusSearch() : null;
                      onDepartureDateChange(index, date);
                      onMultiCityDatePickerClose(index);
                    }
                  "
                  @click.native="onMultiCityDatePickerOpen(index)"
                >
                  <img
                    src="@/assets/images/icons/finance-calendar.png"
                    alt="calendar icon"
                  />
                </CustomDatepickerV3>
              </div>
            </b-field>
          </transition>
        </b-field>
      </div>
    </transition>

    <div
      class="multi-city-button-wrapper"
      :style="{ flexDirection: tripType === 'multi' ? 'column' : 'row' }"
    >
      <transition name="slide-in">
        <div class="buttons" v-if="multiFlights[0].departureDate">
          <button
            @click="addNewmultiFlight"
            class="button is-rounded"
            v-if="tripType == 'multi' && multiFlights.length < 5"
          >
            <b-icon icon="plus-circle" type="is-secondary"></b-icon>
            <span>Add Flight</span>
          </button>
          <button
            @click="removeMulticityFlight"
            class="button is-rounded"
            v-if="tripType == 'multi' && multiFlights.length >= 1"
          >
            <b-icon icon="minus-circle" type="is-warning"></b-icon>
            <span>Remove Flight</span>
          </button>
        </div>
      </transition>

      <!--Dropdown menus-->
      <transition name="slide-in">
        <div
          class="traveller-cabin-container"
          v-if="
            (tripType === 'oneway' && departureDate) ||
              (tripType !== 'oneway' && departureDate && returnDate) ||
              (tripType === 'multi' &&
                multiFlights.length >= 2 &&
                multiFlights.every(
                  (flight) => flight.departureDate && flight.arrivalLocation,
                ))
          "
        >
          <travellers-dropdown
            v-model="travellerCounts"
            @input="updateTravellerCounts"
          />
          <div class="cabin-select">
            <custom-filter-select
              v-on:input="applyFilter()"
              v-model="preferredCabin"
              :options="['Economy', 'Premium Economy', 'Business', 'First']"
            />
          </div>
        </div>
      </transition>
    </div>
    <transition name="slide-in">
      <div
        class="search-button-wrapper"
        v-if="
          (tripType === 'oneway' && departureDate) ||
            (tripType !== 'oneway' && departureDate && returnDate) ||
            (tripType === 'multi' &&
              multiFlights.length >= 2 &&
              multiFlights.every(
                (flight) => flight.departureDate && flight.arrivalLocation,
              ))
        "
      >
        <b-button
          ref="search"
          @click="searchFlights(false)"
          type="is-primary"
          class="search-button"
          data-cy="search-flights-button"
          >Search flights</b-button
        >
      </div>
    </transition>
    <b-message
      type="is-danger"
      v-if="!validPassengers.success || errors.length > 0"
    >
      <div v-for="(error, index) in validPassengers.errors" :key="index">
        {{ error }}
      </div>
      <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
    </b-message>
    <!--Info Blurb-->
    <div class="search-blurb-wrapper">
      <small>
        Before booking your flight(s), please ensure you comply with the
        relevant visa and health regulations for your destination country, as
        well as any transit or technical stops. For more information,
        <a
          @click="invsyLink('https://apply.joinsherpa.com/travel-restrictions')"
          >please visit Sherpa</a
        >.
        <!-- or the
        <a @click="invsyLink('https://www.iatatravelcentre.com/passport-visa-health-travel-document-requirements.htm')"
          >International Air Transport Association’s Travel Centre</a
        >. -->
      </small>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api.service.js";
import debounce from "lodash/debounce";
import { locations } from "@/assets/json/locations.json";
import moment from "moment";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";
import CustomDatepickerV3 from "@/components/CustomDatepickerV3.vue";
import CustomFilterSelect from "@/components/CustomFilterSelect.vue";
import { mapState } from "vuex";
import TravellersDropdown from "@/components/TravellersDropdown.vue";
import { filterAirports, formatAirportLabel } from "@/utils/airportSearch";
import AirportOption from "@/components/AirportOption.vue";

export default {
  name: "SearchEngine",
  data() {
    const today = new Date();
    return {
      departureMonth: null,
      departureYear: null,
      labelPosition: null,
      tripType: "return",
      typeKey: 50,
      minDepartureDate: this.getMinDepartureDate(),
      maxDepartureDate: new Date(
        today.getFullYear(),
        today.getMonth() + 12,
        today.getDate(),
        today.getHours(),
      ),
      allAirports: locations,
      filteredAirports: [],
      departureLocation: {
        airportName: undefined,
        airportCode: undefined,
      },
      arrivalLocation: {
        airportName: undefined,
        airportCode: undefined,
      },
      departureDate: undefined,
      returnDate: undefined,
      travellerCounts: {
        adults: 1,
        youngAdults: 0,
        children: 0,
        infants: 0,
      },
      adults: 1,
      youngAdults: 0,
      children: 0,
      infants: 0,
      preferredCabin: "Economy",
      multiFlights: [
        {
          departureDate: null,
          departureAirport: null,
          arrivalAirport: null,
          departureInputFocused: false,
          arrivalInputFocused: false,
          departureHasText: false,
          arrivalHasText: false,
          dateModalOpen: false,
        },
      ],
      flights: [],
      departureAirportOnly: true,
      arrivalAirportOnly: true,
      departureTime: "Any time",
      errors: [],
      validSearch: true,
      loader: {
        isLoading: false,
        stepCount: 3000,
        backgroundColor: "linear-gradient(to right, lightgray, gray)",
        opacity: 0.5,
        spinnerSize: "22px",
        spinnerColor: "#485DC5",
        currentStep: "Preparing links.",
        progressSteps: ["Connecting to providers.", "Fetching latest flights."],
      },
      refreshKey: 0,
      // Floating label states
      departureDateModalOpen: false,
      returnDateModalOpen: false,
      departureInputFocused: false,
      arrivalInputFocused: false,
      departureHasText: false,
      arrivalHasText: false,
    };
  },
  computed: {
    ...mapState(["priorSearch"]),
    isMaintainence() {
      const currentDate = new Date();
      // Create start and end dates for comparison
      const startYear = currentDate.getFullYear();
      const endYear = currentDate.getFullYear();
      const startMonth = 2; // March (0-indexed)
      const endMonth = 3; // April (0-indexed)
      const startDay = 31;
      const endDay = 1;
      const startHour = 11;
      const endHour = 0;
      const startMinute = 30;
      const endMinute = 0;
      const startDate = new Date(
        startYear,
        startMonth,
        startDay,
        startHour,
        startMinute,
      );
      const endDate = new Date(endYear, endMonth, endDay, endHour, endMinute);
      // Check if the current date falls within the range
      //return currentDate >= startDate && currentDate < endDate;
      return false;
    },
    maintenanceMessage() {
      let customMessage = "";

      if (customMessage) {
        return customMessage;
      }

      return (
        "We're undergoing maintenance on our reservation system. " +
        "Unfortunately, you will be unable to book tickets for flights during this time. " +
        "Please check back in a few minutes. Thank you for your patience."
      );
    },
    notSearch() {
      return this.$route.name == "results";
    },
    validPassengers() {
      let errors = [];
      let success = true;
      if (this.infants + this.adults + this.children + this.youngAdults > 9) {
        errors.push("Maximum of 9 passengers allowed.");
        success = false;
      }
      if (this.infants > this.adults) {
        errors.push("Only one infant per adult passenger allowed.");
        success = false;
      }
      if (this.children > 0 && this.adults === 0) {
        errors.push("Children must be accompanied by an adult.");
        success = false;
      } else if (this.adults === 0) {
        errors.push("At least one adult passenger required");
        success = false;
      }
      return {
        success: success,
        errors: errors,
      };
    },
    isQuarentine() {
      return new Date() < new Date(2020, 6, 1);
    },
    urlSearchParameters() {
      return this.$store.state.urlSearchParameters;
    },
    numberOfPassangers() {
      return this.children + this.infants + this.adults + this.youngAdults;
    },
  },
  mounted() {
    if (this.$route.name === "search") {
      this.$store.commit("notDomesticReturn");
    }
    if (this.priorSearch.exists) {
      this.travellerCounts = {
        adults: this.priorSearch.adults,
        youngAdults: this.priorSearch.youngAdults,
        children: this.priorSearch.children,
        infants: this.priorSearch.infants,
      };
      this.arrivalLocation = this.priorSearch.arrivalLocation;
      this.departureLocation = this.priorSearch.departureLocation;
      this.departureTime = this.priorSearch.departureTime;
      this.adults = this.priorSearch.adults;
      this.youngAdults = this.priorSearch.youngAdults;
      this.children = this.priorSearch.children;
      this.infants = this.priorSearch.infants;
      this.preferredCabin = this.priorSearch.preferredCabin;
      this.departureDate = this.priorSearch.departureDate;
      this.returnDate = this.priorSearch.returnDate;
      this.multiFlights = this.priorSearch.multiFlights;
      // Ensure multiFlights have tracking fields for floating labels
      this.multiFlights = this.multiFlights.map((flight) => ({
        ...flight,
        departureInputFocused: flight.departureInputFocused || false,
        arrivalInputFocused: flight.arrivalInputFocused || false,
        departureHasText: flight.departureHasText || false,
        arrivalHasText: flight.arrivalHasText || false,
        dateModalOpen: flight.dateModalOpen || false,
      }));
      this.tripType = this.priorSearch.tripType;

      this.$refs.arrivalLocation.setSelected(this.priorSearch.arrivalLocation);
      this.$refs.departureLocation.setSelected(
        this.priorSearch.departureLocation,
      );
      this.$nextTick(() => {
        if (this.$refs.arrivalLocation && this.$refs.arrivalLocation.$el) {
          this.$refs.arrivalLocation.$el.querySelector("input").blur();
        }
      });
    } else {
      this.travellerCounts = {
        adults: 1,
        youngAdults: 0,
        children: 0,
        infants: 0,
      };
    }

    // Add global click handler for floating labels
    document.addEventListener("click", (event) => {
      // Check if click is outside date picker elements
      const isDatePickerClick =
        event.target.closest(".datepicker-container") ||
        event.target.closest(".modal-overlay") ||
        event.target.closest(".modal-content");

      if (!isDatePickerClick) {
        this.departureDateModalOpen = false;
        this.returnDateModalOpen = false;
        // Close all multi-city modal states
        this.multiFlights.forEach((flight) => {
          flight.dateModalOpen = false;
        });
      }
    });
  },
  methods: {
    handleDateRangeSelection(startDate, endDate) {
      this.departureDate = startDate;
      this.returnDate = endDate;
      this.focusSearch();
    },
    updateTravellerCounts(counts) {
      this.adults = counts.adults;
      this.youngAdults = counts.youngAdults;
      this.children = counts.children;
      this.infants = counts.infants;
    },
    invsyLink(url) {
      this.$store.dispatch("invsyLink", url);
    },
    mapCabinNameToType(cabinName) {
      let cabins = {
        Economy: "Economy",
        "Premium Economy": "Premium",
        Business: "Business",
        First: "First",
      };
      return cabins[cabinName];
    },
    getMinDepartureDate() {
      let today = new Date();
      let timelimit = this.add_minutes(today, 240);
      if (this.isToday(timelimit)) {
        return new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - 1,
          today.getHours(),
        );
      } else {
        return new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          today.getHours(),
        );
      }
    },
    add_minutes(dt, minutes) {
      return new Date(dt.getTime() + minutes * 60000);
    },
    isToday(someDate) {
      const today = new Date();
      return (
        someDate.getDate() == today.getDate() &&
        someDate.getMonth() == today.getMonth() &&
        someDate.getFullYear() == today.getFullYear()
      );
    },

    lastDate(index) {
      // Check if the index is greater than 0 to ensure there's a
      // previous multi city flight
      return index > 0 && this.multiFlights[index - 1].departureDate
        ? this.multiFlights[index - 1].departureDate
        : new Date();
    },

    displayDate(date) {
      return moment(date).format("MMMM Do YYYY");
    },
    focusArrivalLocation() {
      this.errors = [];
      if (this.$refs.arrivalLocation) {
        this.$refs.arrivalLocation.focus();
      }
    },
    focusOutboundDate() {
      this.errors = [];
    },
    focusReturnDate() {
      this.errors = [];
    },
    focusSearch() {
      this.errors = [];
    },
    addNewmultiFlight: function() {
      if (this.multiFlights.length >= 5) return;

      let newFlightDefaultDate = undefined;
      let newFlightDefaultDeparture = undefined;

      if (this.multiFlights.length > 0) {
        const lastFlight = this.multiFlights[this.multiFlights.length - 1];

        newFlightDefaultDate = lastFlight.departureDate;
        newFlightDefaultDeparture = lastFlight.arrivalLocation;
      }

      const newFlight = {
        departureDate: newFlightDefaultDate,
        departureLocation: newFlightDefaultDeparture,
        arrivalLocation: null,
        departureInputFocused: false,
        arrivalInputFocused: false,
        departureHasText: false,
        arrivalHasText: false,
        dateModalOpen: false,
      };

      this.multiFlights.push(newFlight);
    },
    removeMulticityFlight: function() {
      if (this.multiFlights.length >= 1) {
        this.multiFlights.pop();
      }
    },
    onDepartureDateChange(index, newDate) {
      this.multiFlights[index].departureDate = newDate;
      // Update departure dates of subsequent multi-city flights
      for (let i = index + 1; i < this.multiFlights.length; i++) {
        let currentFlightDate = new Date(this.multiFlights[i].departureDate);
        // Only update if the current flight's departure date is less than the newDate
        if (currentFlightDate < newDate) {
          this.multiFlights[i].departureDate = newDate;
        }
      }
    },
    onArrivalLocationChange(index, selectedAirport) {
      // Update the current flight's arrival location
      this.multiFlights[index].arrivalLocation = selectedAirport;

      // Update the departure location of the next flight, if it exists
      if (index + 1 < this.multiFlights.length) {
        this.multiFlights[index + 1].departureLocation = selectedAirport;
      }
    },
    stepCurrentProgress: function() {
      const it = this.loader.progressSteps[Symbol.iterator]();
      const int = setInterval(() => {
        const next = it.next();
        if (!next.done) {
          this.loader.currentStep = next.value;
        } else {
          clearInterval(int);
        }
      }, this.loader.stepCount);
    },
    constructFlightsArray: function(urlParams = null) {
      let context = urlParams ? urlParams : this;
      if (context.tripType == "return") {
        this.flights.push({
          origin: {
            value: {
              code: context.departureLocation.airportCode,
              type: context.departureLocation.airportName
                .toLowerCase()
                .includes("all")
                ? "city"
                : "airport",
            },
          },
          destination: {
            value: {
              code: context.arrivalLocation.airportCode,
              type: context.arrivalLocation.airportName
                .toLowerCase()
                .includes("all")
                ? "city"
                : "airport",
            },
          },
          departDate: moment(context.departureDate).format("YYYY-MM-DD"),
          returnDate:
            context.tripType == "return"
              ? moment(context.returnDate).format("YYYY-MM-DD")
              : null,
        });
      } else if (context.tripType == "oneway") {
        this.flights.push({
          origin: {
            value: {
              code: context.departureLocation.airportCode,
              type: context.departureLocation.airportName
                .toLowerCase()
                .includes("all")
                ? "city"
                : "airport",
            },
          },
          destination: {
            value: {
              code: context.arrivalLocation.airportCode,
              type: context.arrivalLocation.airportName
                .toLowerCase()
                .includes("all")
                ? "city"
                : "airport",
            },
          },
          departDate: moment(context.departureDate).format("YYYY-MM-DD"),
        });
      } else if (context.tripType == "multi") {
        if (context.multiFlights) {
          context.multiFlights.forEach((flight) => {
            this.flights.push({
              origin: {
                value: {
                  code: flight.departureLocation.airportCode,
                  type: flight.departureLocation.airportName
                    .toLowerCase()
                    .includes("all")
                    ? "city"
                    : "airport",
                },
              },
              destination: {
                value: {
                  code: flight.arrivalLocation.airportCode,
                  type: flight.arrivalLocation.airportName
                    .toLowerCase()
                    .includes("all")
                    ? "city"
                    : "airport",
                },
              },
              departDate: moment(flight.departureDate).format("YYYY-MM-DD"),
            });
          });
        } else {
          if (context.multi1_departureDate) {
            this.flights.push({
              departureDate: moment(context.multi1_departureDate).format(
                "DD/MM/YYYY",
              ),
              departureAirport: context.multi1_departureAirport,
              arrivalAirport: context.multi1_arrivalAirport,
              departureAirportOnly: !this.departureAirportOnly
                ? "false"
                : "true",
              arrivalAirportOnly: !this.arrivalAirportOnly ? "false" : "true",
            });
          }
          if (context.multi2_departureDate) {
            this.flights.push({
              departureDate: moment(context.multi2_departureDate).format(
                "DD/MM/YYYY",
              ),
              departureAirport: context.multi2_departureAirport,
              arrivalAirport: context.multi2_arrivalAirport,
              departureAirportOnly: !this.departureAirportOnly
                ? "false"
                : "true",
              arrivalAirportOnly: !this.arrivalAirportOnly ? "false" : "true",
            });
          }
          if (context.multi3_departureDate) {
            this.flights.push({
              departureDate: moment(context.multi3_departureDate).format(
                "DD/MM/YYYY",
              ),
              departureAirport: context.multi3_departureAirport,
              arrivalAirport: context.multi3_arrivalAirport,
              departureAirportOnly: !this.departureAirportOnly
                ? "false"
                : "true",
              arrivalAirportOnly: !this.arrivalAirportOnly ? "false" : "true",
            });
          }
        }
      }
    },
    formatAirportLabel,
    getAsyncAirports: debounce(function(query) {
      this.errors = [];
      if (query.length) {
        this.filteredAirports = filterAirports(this.allAirports, query);
      }
    }, 300),
    getAutocompleteValue(location) {
      // This method returns the value that should be displayed in the
      // autocomplete input for multi-city flights
      return location ? formatAirportLabel(location) : "";
    },
    getLocationFromAirportCode(code) {
      return this.allAirports.find((location) => location.airportCode == code);
    },
    isSouthAfricanAirport(airportCode) {
      let location = this.allAirports.find((airport) => {
        return airport.airportCode == airportCode;
      });
      if (location.country.toLowerCase() == "south africa") {
        return true;
      } else {
        return false;
      }
    },
    validSearchCheck(urlParams = null) {
      this.errors = []; // Clear existing errors
      let context = urlParams ? urlParams : this;
      // handle multi city search
      if (context.tripType == "multi") {
        this.departureLocation = context.multiFlights[0].departureLocation;
        this.arrivalLocation = context.multiFlights[0].arrivalLocation;
        this.departureDate = context.multiFlights[0].departureDate;
        if (context.multiFlights.length < 2) {
          this.errors.push("Multi-city trips require at least two flights.");
          return false;
        }
        for (let i = 0; i < context.multiFlights.length; i++) {
          if (!context.multiFlights[i].departureLocation.airportCode) {
            this.errors.push(
              `Please select a departure location for leg ${i + 1}.`,
            );
            return false;
          }
          if (!context.multiFlights[i].arrivalLocation.airportCode) {
            this.errors.push(
              `Please select an arrival location for leg ${i + 1}.`,
            );
            return false;
          }
          if (!context.multiFlights[i].departureDate) {
            this.errors.push(
              `Please select a departure date for leg ${i + 1}.`,
            );
            return false;
          }
          // Validate same city for multi-city trips
          if (
            context.multiFlights[i].departureLocation.city ==
            context.multiFlights[i].arrivalLocation.city
          ) {
            this.errors.push(
              `Origin and destination cannot be in the same city for leg ${i +
                1}.`,
            );
            return false;
          }
          // Validate date sequence for multi-city trips
          if (
            i > 0 &&
            context.multiFlights[i].departureDate &&
            context.multiFlights[i - 1].departureDate
          ) {
            if (
              new Date(context.multiFlights[i].departureDate) <
              new Date(context.multiFlights[i - 1].departureDate)
            ) {
              this.errors.push(
                `Departure date for leg ${i +
                  1} cannot be before the departure date of leg ${i}.`,
              );
              return false;
            }
          }
        }
      } else if (
        !context.departureLocation.airportCode ||
        !context.arrivalLocation.airportCode ||
        !context.departureDate
      ) {
        this.errors.push("All search fields required.");
        return false;
      } else if (context.tripType == "return" && !context.returnDate) {
        this.errors.push("Please select a return date.");
        return false;
      } else if (
        context.departureLocation.city == context.arrivalLocation.city
      ) {
        this.errors.push("Origin and destination cannot be in the same city.");
        return false;
      }
      return true;
    },
    async searchFlights(isUrlSearch) {
      if (!this.validPassengers.success || !this.validSearchCheck()) {
        this.$buefy.toast.open({
          message: "Invalid Search!",
          type: "is-warning",
          position: "is-top",
        });
        return false;
      }
      this.$store.commit("recordSearch", {
        exists: true,
        arrivalLocation: this.arrivalLocation,
        departureLocation: this.departureLocation,
        departureTime: this.departureTime,
        adults: this.adults,
        youngAdults: this.youngAdults,
        children: this.children,
        infants: this.infants,
        preferredCabin: this.preferredCabin,
        departureDate: this.departureDate,
        returnDate: this.returnDate,
        multiFlights: this.multiFlights,
        tripType: this.tripType,
      });
      if (!isUrlSearch) {
        this.constructFlightsArray();
      }
      this.$store.dispatch("resetAll");
      let searchBody = this.createSearchBody();
      this.$store.commit("setSearchParameters", searchBody);
      // Fallback redirect to /results for slow searches; cancelled in `finally`
      // once the search resolves so it can't fire later and yank the user back
      // to /results after they've already moved on to /details.
      let resultsRedirect;
      try {
        this.loader.isLoading = true;
        if (this.$route.name === "results") {
          this.loader.isLoading = false;
          this.$parent.close();
        }
        console.time("search request time:");
        this.$store.commit("toggleSearchInProgress", true);
        this.stepCurrentProgress();
        resultsRedirect = setTimeout(() => {
          if (this.$route.name !== "results") {
            this.$router.push("/results");
          }
        }, this.loader.stepCount * 3);
        this.checkForDomesticFlight();
        const result = await Api.search(searchBody);
        this.$store.commit("setSearchResults", result.data.response);
        if (this.$route.name !== "results") {
          this.$router.push("/results");
        }
        this.$store.commit("orderResults", "Price");
      } catch (error) {
        this.$store.commit("setSearchResults", { errors: error });
      } finally {
        clearTimeout(resultsRedirect);
        console.timeEnd("search request time:");
        this.$store.commit("toggleSearchInProgress", false);
      }
    },
    createSearchBody() {
      return {
        tripType: this.tripType,
        sessionID: this.$store.state.session,
        travellers: {
          adults: this.adults,
          youngAdults: this.youngAdults,
          children: this.children,
          infants: this.infants,
        },
        moreOptions: {
          preferredCabins: {
            value: this.mapCabinNameToType(this.preferredCabin),
            display: this.mapCabinNameToType(this.preferredCabin),
          },
          isCalendarSearch: false,
        },
        itineraries: this.flights,
      };
    },
    checkForDomesticFlight() {
      // For each itinerary in flights array, check if orgin.value.code and destination.value.code are in South Africa from locations list.
      // If not, set isInternational to true.
      let isInternational = false;
      this.flights.forEach((itinerary) => {
        if (
          !this.isSouthAfrica(itinerary.origin.value.code) ||
          !this.isSouthAfrica(itinerary.destination.value.code)
        ) {
          isInternational = true;
        }
      });
      this.$store.commit("setIsInternational", isInternational);
    },
    isSouthAfrica(code) {
      let location = this.allAirports.find((airport) => {
        return airport.airportCode == code;
      });
      if (location.country.toLowerCase() == "south africa") {
        return true;
      } else {
        return false;
      }
    },
    // Datepicker helpers
    handleDepartureSelection(month, year) {
      this.departureMonth = month;
      this.departureYear = year;
      this.refreshKey++;
    },
    // Floating label methods
    onDepartureDatePickerOpen() {
      this.departureDateModalOpen = true;
    },
    onDepartureDatePickerClose() {
      this.departureDateModalOpen = false;
    },
    onReturnDatePickerOpen() {
      this.returnDateModalOpen = true;
    },
    onReturnDatePickerClose() {
      this.returnDateModalOpen = false;
    },
    onMultiCityDatePickerOpen(index) {
      this.multiFlights[index].dateModalOpen = true;
    },
    onMultiCityDatePickerClose(index) {
      this.multiFlights[index].dateModalOpen = false;
    },
  },
  watch: {
    departureDate: function(newVal) {
      // Only update return date if already set and now invalid
      if (this.returnDate && this.returnDate < newVal) {
        this.returnDate = newVal;
      }
      // Don't automatically set return date to be the same as departure date
    },
    urlSearchParameters: function(newVal) {
      // if url parameters have been set, generate correct data format
      // and make instant search
      console.log("automatically calling search from url parameters");
      this.departureLocation = this.getLocationFromAirportCode(
        newVal.departureAirport,
      );
      this.arrivalLocation = this.getLocationFromAirportCode(
        newVal.arrivalAirport,
      );
      newVal.departureLocation = this.getLocationFromAirportCode(
        newVal.departureAirport,
      );
      newVal.arrivalLocation = this.getLocationFromAirportCode(
        newVal.arrivalAirport,
      );
      if (newVal.multi1_departureAirportl) {
        newVal.multi1_departureAirport = this.getLocationFromAirportCode(
          newVal.multi1_departureAirport,
        );
        newVal.multi1_arrivalAirport = this.getLocationFromAirportCode(
          newVal.multi1_arrivalAirport,
        );
      }
      if (newVal.multi2_departureAirportl) {
        newVal.multi2_departureAirport = this.getLocationFromAirportCode(
          newVal.multi2_departureAirport,
        );
        newVal.multi2_arrivalAirport = this.getLocationFromAirportCode(
          newVal.multi2_arrivalAirport,
        );
      }
      if (newVal.multi3_departureAirportl) {
        newVal.multi3_departureAirport = this.getLocationFromAirportCode(
          newVal.multi3_departureAirport,
        );
        newVal.multi3_arrivalAirport = this.getLocationFromAirportCode(
          newVal.multi3_arrivalAirport,
        );
      }
      this.tripType = newVal.tripType;
      this.adults = Number(newVal.adults);
      this.youngAdults = Number(newVal.youngAdults);
      this.children = Number(newVal.children);
      this.infants = Number(newVal.infants);
      this.preferredCabin = newVal.preferredCabin;
      this.constructFlightsArray(newVal);
      this.searchFlights(true);
    },
    tripType(newType, oldType) {
      if (
        oldType === "multi" &&
        (newType === "return" || newType === "oneway")
      ) {
        if (this.multiFlights.length > 0 && this.multiFlights[0]) {
          const firstLeg = this.multiFlights[0];

          if (
            firstLeg.departureLocation &&
            firstLeg.departureLocation.airportCode
          ) {
            this.departureLocation = { ...firstLeg.departureLocation };
            this.$nextTick(() => {
              if (
                this.$refs.departureLocation &&
                typeof this.$refs.departureLocation.setSelected === "function"
              ) {
                this.$refs.departureLocation.setSelected(
                  this.departureLocation,
                );
              }
            });
          }
          if (
            firstLeg.arrivalLocation &&
            firstLeg.arrivalLocation.airportCode
          ) {
            this.arrivalLocation = { ...firstLeg.arrivalLocation };
            this.$nextTick(() => {
              if (
                this.$refs.arrivalLocation &&
                typeof this.$refs.arrivalLocation.setSelected === "function"
              ) {
                this.$refs.arrivalLocation.setSelected(this.arrivalLocation);
              }
            });
          }

          // Transfer departure date from multi-city to one-way/return
          if (firstLeg.departureDate) {
            this.departureDate = firstLeg.departureDate;
          }
        }
        // Blur both fields after the transition with a small delay
        this.$nextTick(() => {
          setTimeout(() => {
            if (
              this.$refs.departureLocation &&
              this.$refs.departureLocation.$el
            ) {
              const departureInput = this.$refs.departureLocation.$el.querySelector(
                "input",
              );
              if (departureInput) {
                departureInput.blur();
              }
            }
            if (this.$refs.arrivalLocation && this.$refs.arrivalLocation.$el) {
              const arrivalInput = this.$refs.arrivalLocation.$el.querySelector(
                "input",
              );
              if (arrivalInput) {
                arrivalInput.blur();
              }
            }
          }, 100);
        });
      } else if (
        (oldType === "return" || oldType === "oneway") &&
        newType === "multi"
      ) {
        let firstLeg;
        if (this.multiFlights.length > 0 && this.multiFlights[0]) {
          firstLeg = this.multiFlights[0];
        } else {
          firstLeg = {
            departureDate: null,
            departureLocation: null,
            arrivalLocation: null,
            departureInputFocused: false,
            arrivalInputFocused: false,
            departureHasText: false,
            arrivalHasText: false,
            dateModalOpen: false,
          };
          if (this.multiFlights.length > 0) {
            this.$set(this.multiFlights, 0, firstLeg);
          } else {
            this.multiFlights.push(firstLeg);
          }
        }
        if (this.departureLocation && this.departureLocation.airportCode) {
          firstLeg.departureLocation = { ...this.departureLocation };
        }

        if (this.arrivalLocation && this.arrivalLocation.airportCode) {
          firstLeg.arrivalLocation = { ...this.arrivalLocation };
        }

        // Transfer departure date from one-way/return to multi-city
        if (this.departureDate) {
          // Check if transferring this date would conflict with subsequent legs
          const hasConflict =
            this.multiFlights.length > 1 &&
            this.multiFlights
              .slice(1)
              .some(
                (flight) =>
                  flight.departureDate &&
                  new Date(this.departureDate) >=
                    new Date(flight.departureDate),
              );

          if (!hasConflict) {
            firstLeg.departureDate = this.departureDate;
          }
          // If we can't transfer the date due to conflicts, leave the existing firstLeg.departureDate as is
        }
      }
    },
  },
  components: {
    ClipLoader,
    CustomDatepickerV3,
    CustomFilterSelect,
    TravellersDropdown,
    AirportOption,
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/styles/settings.scss";

body {
  background-color: red;
}

/* Additional specificity for Bulma override */
::v-deep .message.is-danger {
  .message-body {
    background: transparent !important;
  }
}

.search_engine {
  background: $light;
  margin: 0.75rem;
  padding: 40px;
  max-width: 812px;
  background: #ffffff;
  box-shadow: 0px -1px 4px rgba(0, 0, 0, 0.05),
    0px 2px 8px -2px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  display: flex;
  gap: 24px;
  flex-direction: column;
}

.add-overflow-y {
  overflow-y: scroll;
}

.loading-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden !important;
  height: 100%;
}

.prompt-header {
  font-size: 1em;
  text-align: center;
  color: #555555;
  margin-top: 1rem;
  padding: 1rem;
  span {
    font-size: 1.2em;
    font-style: normal;
    font-weight: bold;
    color: $primary-color;
    line-height: 1.6em;
  }
}

.prompt-loader {
  margin-top: 3rem;
}

.easy-flex {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.search-engine-heading {
  margin-bottom: 16px;
  h1 {
    font-family: "Libre Caslon Text", serif;
    font-weight: 400;
    font-size: 40px;
    line-height: 48px;
    color: #30384a;
  }
}
.radio-button-wrapper {
  display: flex;
  flex-direction: row;
  gap: 24px;
}

.location-wrapper {
  display: flex;
  flex-direction: row;
  gap: 24px;
  justify-content: space-between;
  .field {
    flex: 1;
    margin-right: 10px;

    &:last-child {
      margin-right: 0;
    }
  }
}
div.buttons {
  display: flex;
}

.multi-city-button-wrapper {
  // margin-bottom: 32px;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
}

.multi-flight-form {
  margin-top: 16px;
  ::v-deep div.field-body {
    margin-bottom: 32px;
  }
  ::v-deep label.label {
    margin-bottom: 16px;
  }
}

.dropdown-wrapper {
  display: flex;
  flex-direction: row;
  // flex-basis: 100%;
  margin-bottom: 0;
  width: 348px;
}
// Autocomplete dropdown
.dropdown-content {
  overflow-y: auto; // Show vertical scrollbar only when necessary
  max-height: 300px; // Set a max height to define when the vertical scrollbar appears
}
.media-content {
  overflow-x: hidden; // Prevent horizontal scrolling
}
.time-picker-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 24px;
}

.traveller-cabin-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 24px;
  justify-content: space-between;
}

// .traveller-cabin-container .field {
//   width: auto; /* reset width */
//   flex: 1;
// }
// .traveller-cabin-container .field-body {
//   display: flex;
//   flex-direction: column;
// }

// Datepicker input styles

.time-picker-container .field {
  width: auto; /* reset width */
  flex: 1;
}
.time-picker-container .field-body {
  display: flex;
  flex-direction: column;
}

.datepicker-container {
  padding-right: 0 !important;
}

.search-button-wrapper {
  display: flex;
  justify-content: flex-end;

  .search-button {
    font-size: 16px;
    line-height: 24px;
    color: #ffffff;
    background-color: #30384a;
    padding: 0 12px;
    border-radius: 24px;
  }
}

.search-blurb-wrapper {
  display: flex;
  align-items: center;
  border-left: 4px solid $brandOranges;
  margin-top: 0;

  small {
    padding-left: 16px;
    padding-right: 16px;
    color: #30384a;
    font-weight: 400;
  }

  small,
  small a {
    font-size: 12px;
    line-height: 20px;
    font-family: "Inter", sans-serif;
  }
  small a {
    color: $brandOranges;
    font-weight: 600;
  }
}

//tablet to smarthphone screen sizes
@media (max-width: 780px) {
  .radio-button-wrapper {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  .search-engine-heading {
    margin-bottom: 32px;
    h1 {
      font-size: 32px;
    }
  }
  .location-wrapper {
    flex-direction: column !important;
  }
  .time-picker-container {
    flex-direction: column !important;
  }
  .traveller-cabin-container {
    flex-direction: column !important;
  }

  .autocomplete {
    width: 100% !important;
  }
  .cabin-select {
    width: 100% !important;
  }

  div.dropdown .dropdown-trigger {
    width: 100% !important;
  }

  .add-overflow-y {
    overflow-y: scroll;
    margin-bottom: 45px;
    margin-top: 26px;
  }

  .search_engine {
    padding: 25px;
    margin-right: 0;
    margin-left: 0;
  }

  /* Fix floating label positioning for mobile vertical stack */
  .location-wrapper {
    width: 100%;
    padding: 0;
  }

  .location-wrapper .field {
    width: 100%;
    margin-right: 0;
  }

  /* Target b-autocomplete to ensure it takes full width */
  .location-wrapper ::v-deep .autocomplete,
  .location-wrapper ::v-deep .autocomplete .control {
    width: 100% !important;
  }

  /* On mobile, position both labels relative to their autocomplete fields */
  .floating-label.departure-location-label {
    left: 16px;
    /* Position relative to first autocomplete */
  }

  .floating-label.arrival-location-label {
    left: 16px !important;
    top: calc(32px + 64px + 24px); /* 32px center + field height + gap */
  }

  .floating-label.arrival-location-label.floating-label-active {
    top: calc(5px + 64px + 24px); /* Active position + field height + gap */
  }

  /* Multi-city mode: "To" labels are within the same flight leg, not offset from main layout */
  .has-field-margin .floating-label.arrival-location-label {
    top: 125px; /* 32px center + field height + gap (adjusted for multi-city) */
    left: 16px !important;
  }

  .has-field-margin
    .floating-label.arrival-location-label.floating-label-active {
    top: 97px; /* Active position (adjusted from 93px for multi-city) */
    left: 16px !important;
  }

  /* Fix date picker label positioning for mobile vertical stack - SearchEngine only */
  .search_engine .floating-label.departure-label {
    left: 16px;
    /* Position relative to first date picker */
  }

  .search_engine .floating-label.return-label {
    left: 16px !important;
    /* Position relative to second date picker - need to offset by height of first field + gap */
    top: calc(
      32px + 64px + 24px
    ) !important; /* 32px center + field height + gap */
  }

  .search_engine .floating-label.return-label.floating-label-active {
    top: calc(
      5px + 64px + 24px
    ) !important; /* Active position + field height + gap */
    left: 16px !important;
  }
}

// Small mobile devices
@media (max-width: 375px) {
  .search_engine {
    width: 98%;
  }
  .search-engine-heading {
    h1 {
      font-size: 32px;
      line-height: 38px;
    }
  }
  .radio-button-wrapper {
    flex-direction: column;
    margin-bottom: 48px;
    label.b-radio:not(:last-child) {
      margin-bottom: 16px;
    }
  }
  .location-wrapper {
    flex-direction: column; // stack the items vertically on smaller screens
    .field {
      flex-basis: 100%;
      margin-right: 0 !important;
    }
    .field.is-expanded:nth-of-type(2) {
      margin-top: 16px !important;
    }
  }

  .time-picker-container .field-body {
    flex-direction: column;
  }
  .time-picker-container .field {
    width: 100%;
    flex-basis: 100%;
  }

  .time-picker-container .field-body {
    flex-direction: column;
  }

  div.buttons,
  div.dropdown-wrapper {
    flex-direction: column;
    box-sizing: border-box;
    overflow: hidden;
    width: auto;
    display: flex;
    & > * {
      flex-shrink: 1;
    }
  }

  div.dropdown-wrapper div.dropdown div.dropdown-trigger button.button {
    display: flex;
    justify-content: space-between;
    width: 100%;
    span.icon {
      margin-left: 0;
    }
  }

  // div.dropdown .dropdown-trigger {
  //   width: 100%;
  // }

  .traveller-cabin-container .field-body {
    flex-direction: column;
  }
  .traveller-cabin-container .field {
    width: 100%;
    flex-basis: 100%;
  }
  .search-button {
    width: 100%;
  }
}

::v-deep .cabin-select {
  .select-text {
    font-family: inherit; /* Use the same font as parent */
    font-weight: 400; /* Match the Travellers dropdown */
  }
  .option {
    padding: 16px;
  }
  .selected-option-container {
    padding: 7px;
  }
}

.one-way-only ::v-deep #one-way-only {
  margin-right: 0 !important;
}

.label {
  margin-top: 5px;
}

/* Position labels correctly over each location field */
.floating-label.departure-location-label {
  left: 16px;
}

.floating-label.arrival-location-label {
  left: calc(50% + 34px); /* 50% + half of gap (24px) + padding */
}

/* Ensure no internal placeholder text shows through for autocomplete fields */
.location-wrapper .autocomplete .control .input::placeholder,
.location-wrapper .autocomplete .control .input::-webkit-input-placeholder,
.location-wrapper .autocomplete .control .input::-moz-placeholder,
.location-wrapper .autocomplete .control .input:-ms-input-placeholder {
  color: transparent !important;
}
</style>

<style lang="scss">
// Unscoped styles to override the Buefy default inputs styles
@import "../assets/styles/settings.scss";

.modal-background {
  background: #f0efed !important;
}

// Radio button styles
.b-radio.radio input[type="radio"]:checked + span.check {
  border-color: #30384a;

  &::before {
    background-color: #30384a;
    height: 16px;
    width: 16px;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
  }
}

.b-radio.radio input[type="radio"] + span.check {
  height: 20px;
  width: 20px;
  position: relative;
  border: 2px solid #d1d2d6;
}

.b-radio.radio input[type="radio"]:checked + span.check {
  border-color: #30384a;
}

.b-radio.radio:hover span.check {
  border-color: #30384a !important;
}

// Autocomplete styles
.autocomplete {
  width: 348px;
  .input {
    height: 64px;
    background-color: #f6f6f4;
    box-shadow: none;
    border: none;
    border-radius: 8px !important;
    padding: 0px 16px;

    &:focus {
      border: none;
      outline: none;
    }
    &:active {
      border: none;
      outline: none;
    }
    // Style the placeholder text
    &::placeholder,
    &::-webkit-input-placeholder,
    &::-moz-placeholder,
    &:-ms-input-placeholder {
      color: $light-grey;
    }
  }

  // Add padding-top when label is active (when user is typing or field has value)
  &.has-active-label .input {
    padding: 24px 16px 8px 16px;
  }
}
// Dropdown menu styles
div.dropdown {
  flex-basis: 100%;

  .dropdown-trigger {
    appearance: none; // attempt to remove browser default styles
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: #f6f6f4 !important;
    height: 64px !important;
    border-radius: 8px !important;
    border: none !important;
    -webkit-box-shadow: none !important;
    box-shadow: none !important;
    font-size: 16px !important;
    width: 348px;
    display: flex;
    align-items: center;
    // padding: 16px 12px;
    justify-content: space-between;
    cursor: pointer;

    &:hover,
    &:focus {
      outline: none;
      box-shadow: none;
    }

    button.button {
      width: 100%;
      background-color: #f6f6f4;
      border: none;
      // margin-top: 10px;
      display: flex;
      justify-content: space-between;

      span.icon {
        margin-left: auto;
      }
    }
  }
}

div.field.last,
div.field.last.is-expanded
  div.datepicker
  div.dropdown
  div.dropdown-trigger
  div.control {
  input {
    border: none;
    background-color: #f6f6f4;
    box-shadow: none;
    // Style the placeholder text
    &::placeholder,
    &::-webkit-input-placeholder,
    &::-moz-placeholder,
    &:-ms-input-placeholder {
      color: $light-grey;
    }
  }
}

.dropdown-menu {
  max-height: calc(100vh - 10px);
}

.is-rounded {
  background-color: #30384a !important;
  color: white !important;
}

.cabin-select {
  width: 348px;
  // padding-left: 7px;
}

//Tablet
@media (max-width: 780px) {
  .cabin-select {
    width: 100%;
  }

  /* Fix floating label positioning for mobile vertical stack */
  .floating-label.return-label {
    left: 16px !important;
  }
}

@media (max-width: 375px) {
  .cabin-select {
    width: 100%t;
  }
  .dropdown-wrapper {
    width: 100% !important;
  }

  div.dropdown .dropdown-trigger {
    width: 100% !important;
  }
}

/* mobile styles */
@media (max-width: 767px) {
  .autocomplete {
    width: 100%;
  }
  .cabin-select {
    width: 100%;
  }
  .dropdown-wrapper {
    width: 100% !important;
  }

  div.dropdown .dropdown-trigger {
    width: 100% !important;
  }
}

.slide-in {
  animation: slide-in-top 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.slide-in-enter-active {
  animation: slide-in-top 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.field.is-grouped.is-grouped-multiline:last-child {
  row-gap: 24px;
}

.datepicker-container:nth-of-type(1) .datepicker-icon[data-v-4e40049a] {
  right: 16px;
  position: absolute;
}

.label {
  padding-top: 8px;
}

/* Floating label styles */
.floating-label {
  position: absolute;
  left: 16px;
  top: 32px; /* Center in 64px input field: 64px / 2 = 32px */
  transform: translateY(-50%);
  font-size: 16px;
  color: $light-grey;
  pointer-events: none;
  // transition: all 0.2s ease;
  z-index: 5;
  background: transparent;
}

.floating-label-active {
  top: 5px;
  left: 16px;
  font-size: 12px;
  color: $light-grey;
  font-weight: 500;
  transform: translateY(0);
}

/* Position labels correctly over each date picker */
.floating-label.departure-label {
  left: 16px;
}

.floating-label.return-label {
  left: calc(50% + 34px); /* 50% + half of gap (24px) + padding */
}

/* Desktop: Ensure return label active state uses standard positioning - SearchEngine only */
.search_engine .floating-label.return-label.floating-label-active {
  top: 5px;
  left: calc(50% + 34px);
}

/* Ensure no internal placeholder text shows through */
.time-picker-container .datepicker-input::placeholder,
.time-picker-container .datepicker-input::-webkit-input-placeholder,
.time-picker-container .datepicker-input::-moz-placeholder,
.time-picker-container .datepicker-input:-ms-input-placeholder {
  color: transparent !important;
}

/* Add padding to datepicker inputs when label is active */
.time-picker-container .has-active-label .datepicker-input {
  padding: 24px 16px 8px 16px !important;
}

/* Add padding to multi-city datepicker inputs when label is active */
.multicity-date-input .has-active-label .datepicker-input {
  padding: 24px 16px 8px 16px !important;
}
</style>
